import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core'
import {
  CombinedError,
  createClient,
  createRequest,
  ssrExchange as getSsrExchange,
  fetchExchange,
} from '@urql/core'
import type { ClientOptions } from '@urql/core'
import { offlineExchange as getOfflineExchange } from '@urql/exchange-graphcache'
import type { Cache } from '@urql/exchange-graphcache'
import { makeDefaultStorage } from '@urql/exchange-graphcache/default-storage'
import { relayPagination } from '@urql/exchange-graphcache/extras'
import { requestPolicyExchange } from '@urql/exchange-request-policy'
import { devtoolsExchange } from '@urql/devtools'
import type { AnyVariables, UseQueryArgs, UseQueryState } from '@urql/vue'
import { useClientHandle, useQuery } from '@urql/vue'
import type { DocumentNode } from 'graphql'
import type { useRuntimeConfig } from 'nuxt/app'
import { ref, toValue } from 'vue'

import type { FragmentType } from '~~/gql/generated'
import type { GraphCacheConfig, Maybe } from '~~/gql/generated/graphcache'
import schema from '~~/gql/generated/introspection'
import {
  allPreferenceEventCategoriesQuery,
  getPreferenceEventCategoryItem,
} from '~~/shared/utils/preference/eventCategory'
import {
  allPreferenceEventFormatsQuery,
  getPreferenceEventFormatItem,
} from '~~/shared/utils/preference/eventFormat'
import {
  allPreferenceEventLocationsQuery,
  getPreferenceEventLocationItem,
} from '~~/shared/utils/preference/eventLocation'

// A `useQuery` that settles on the server: the query is paused at creation,
// and awaiting the response executes it exactly once and fills its state, so
// the page and its head can be rendered with the data. The client behaves
// like a plain `useQuery`
export const useApiQuery = <S, V extends AnyVariables = AnyVariables>(
  args: UseQueryArgs<S, V>,
) => {
  const response = useQuery({
    ...args,
    ...(import.meta.server ? { pause: true } : {}),
  })

  if (import.meta.server) {
    const { client } = useClientHandle()
    const request = createRequest(
      toValue(args.query),
      // `toValue` resolves the variables' ref, but the optional `variables`
      // prop of the query args types them as `V | undefined`
      toValue(args.variables) as V,
    )
    // A promise must never resolve to a thenable: the response itself is
    // one, so the state is returned without its `then`, or the resolution
    // would re-trigger it in an endless adoption loop
    const settle = (): Promise<UseQueryState<S, V>> =>
      client
        .executeQuery(request)
        .toPromise()
        .then(
          (result) => {
            response.data.value = result.data ?? undefined
            response.error.value = result.error ?? undefined
            response.fetching.value = false
            response.stale.value = false
            const { then: _then, ...state } = response
            return state
          },
          (error) => {
            response.error.value = new CombinedError(error)
            response.fetching.value = false
            const { then: _then, ...state } = response
            return state
          },
        )

    let promise: Promise<UseQueryState<S, V>>
    response.then = (onFulfilled, onRejected) => {
      promise ??= settle()
      return promise.then(onFulfilled, onRejected)
    }
  }

  return response
}

type RelayConnection<T> = {
  nodes: T[]
  // ... TODO: add other pagination fields on demand
}

type QueryData<T> = {
  [listKey: string]: RelayConnection<T>
}

// Postgraphile's `...ByRowId` delete mutations only ever supply `rowId`,
// never the Relay `id` graphcache keys by default, so any type reachable
// through one of those mutations has to be keyed by `rowId` too, or
// `invalidateCache` below can't resolve a key and `cache.invalidate` throws.
const keyByRowId = (data: { rowId?: string | null }) => data.rowId ?? null

const invalidateCache = (
  cache: Cache,
  name: string,
  args?: {
    input: { rowId: string | number | null } | { id: string | number | null }
  },
) =>
  args
    ? cache.invalidate({
        __typename: name,
        ...('rowId' in args.input
          ? { rowId: args.input.rowId }
          : { id: args.input.id }),
      })
    : cache
        .inspectFields('Query')
        .filter((field) => field.fieldName === name)
        .forEach((field) => {
          cache.invalidate('Query', field.fieldKey)
        })

const cacheListAppend = <
  Fragment,
  FragmentRaw,
  Query extends QueryData<FragmentRaw>,
  Result,
>({
  cache,
  getItemCreated,
  listKey,
  query,
  result,
}: {
  cache: Cache
  getItemCreated: (result: Result) => Maybe<Fragment> | undefined
  listKey: keyof Query & string
  query: DocumentNode
  result: Result
}) => {
  const newNode = getItemCreated(result)
  if (!newNode) return

  cache.updateQuery<Query>({ query }, (data) => {
    if (!data?.[listKey]) return data

    return {
      ...data,
      [listKey]: {
        ...data[listKey],
        nodes: [newNode, ...data[listKey].nodes],
      },
    }
  })
}

const cacheListRemove = <
  T extends { id: string } | { nodeId: string } | undefined | null,
  ListQuery extends QueryData<FragmentType<DocumentTypeDecoration<T, unknown>>>,
  MutationResult,
>({
  cache,
  getItemDeletedId,
  getItemOfList,
  listKey,
  query,
  result,
}: {
  cache: Cache
  getItemDeletedId: (result: MutationResult) => Maybe<string> | undefined
  getItemOfList: (
    fragment?: FragmentType<DocumentTypeDecoration<T, unknown>>,
  ) => { id: string } | { nodeId: string } | undefined | null
  listKey: keyof ListQuery & string
  query: DocumentNode
  result: MutationResult
}) => {
  const deletedId = getItemDeletedId(result)
  if (!deletedId) return

  cache.updateQuery<ListQuery>({ query }, (data) => {
    if (!data?.[listKey]) return data

    return {
      ...data,
      [listKey]: {
        ...data[listKey],
        nodes: data[listKey].nodes.filter((el) => {
          const unwrapped = getItemOfList(el)
          if (!unwrapped) return true
          if ('id' in unwrapped && unwrapped.id === deletedId) return false
          if ('nodeId' in unwrapped && unwrapped.nodeId === deletedId)
            return false
          return true
        }),
      },
    }
  })
}

export const getUrqlClient = async ({
  clientOptions,
  runtimeConfig,
  baseUrl,
}: {
  clientOptions?: Partial<ClientOptions>
  runtimeConfig: ReturnType<typeof useRuntimeConfig>
  baseUrl: string
}) => {
  const ssrExchange = getSsrExchange({
    isClient: import.meta.client,
  })

  const graphCacheConfig: GraphCacheConfig = {
    keys: {
      Contact: (data) => keyByRowId(data),
      EventFavorite: (data) => keyByRowId(data),
      GeographyPoint: () => null,
      Guest: (data) => keyByRowId(data),
      ProfilePicture: (data) => keyByRowId(data),
    },
    schema,
    resolvers: {
      Query: {
        allAccounts: relayPagination(),
        allContacts: relayPagination(),
        allEvents: relayPagination(),
        allGuests: relayPagination(),
        allUploads: relayPagination(),
      },
    },
    updates: {
      Mutation: {
        // create
        createContact: (_result, _args, cache, _info) =>
          invalidateCache(cache, 'allContacts'),
        createEvent: (_result, _args, cache, _info) => {
          invalidateCache(cache, 'allEvents')
          invalidateCache(cache, 'eventSearch')
        },
        createGuests: (_result, _args, cache, _info) =>
          invalidateCache(cache, 'allGuests'),
        createEventFavorite: (result, _args, cache, _info) => {
          const newNode = result.createEventFavorite?.eventFavorite
          if (!newNode?.eventByEventId?.id) return

          cache.invalidate(
            { __typename: 'Event', id: newNode.eventByEventId.id },
            'eventFavoritesByEventId',
            { first: 1 },
          )
        },
        createEventCategoryMapping: (result, _args, cache, _info) => {
          const eventId =
            result.createEventCategoryMapping?.eventCategoryMapping
              ?.eventByEventId?.id
          if (!eventId) return

          cache.invalidate(
            { __typename: 'Event', id: eventId },
            'eventCategoryMappingsByEventId',
          )
        },
        createEventFormatMapping: (result, _args, cache, _info) => {
          const eventId =
            result.createEventFormatMapping?.eventFormatMapping?.eventByEventId
              ?.id
          if (!eventId) return

          cache.invalidate(
            { __typename: 'Event', id: eventId },
            'eventFormatMappingsByEventId',
          )
        },
        createPreferenceEventCategory: (result, _args, cache, _info) =>
          cacheListAppend({
            cache,
            getItemCreated: (result) =>
              result.createPreferenceEventCategory?.preferenceEventCategory,
            listKey: 'allPreferenceEventCategories',
            query: allPreferenceEventCategoriesQuery,
            result,
          }),
        createPreferenceEventFormat: (result, _args, cache, _info) =>
          cacheListAppend({
            cache,
            getItemCreated: (result) =>
              result.createPreferenceEventFormat?.preferenceEventFormat,
            listKey: 'allPreferenceEventFormats',
            query: allPreferenceEventFormatsQuery,
            result,
          }),
        createPreferenceEventLocation: (result, _args, cache, _info) =>
          cacheListAppend({
            cache,
            getItemCreated: (result) =>
              result.createPreferenceEventLocation?.preferenceEventLocation,
            listKey: 'allPreferenceEventLocations',
            query: allPreferenceEventLocationsQuery,
            result,
          }),

        // delete
        deleteContactByRowId: (_result, args, cache, _info) =>
          invalidateCache(cache, 'Contact', args),
        eventDelete: (_result, _args, cache, _info) => {
          invalidateCache(cache, 'allEvents')
          invalidateCache(cache, 'eventSearch')
        },
        deleteGuestByRowId: (_result, args, cache, _info) =>
          invalidateCache(cache, 'Guest', args),
        deleteEventFavoriteByRowId: (_result, args, cache, _info) =>
          invalidateCache(cache, 'EventFavorite', args),
        deleteEventCategoryMappingByEventIdAndCategoryId: (
          result,
          _args,
          cache,
          _info,
        ) => {
          const payload =
            result.deleteEventCategoryMappingByEventIdAndCategoryId
          if (!payload) return

          if (payload.deletedEventCategoryMappingId)
            cache.invalidate({
              __typename: 'EventCategoryMapping',
              id: payload.deletedEventCategoryMappingId,
            })

          const eventId = payload.eventCategoryMapping?.eventByEventId?.id
          if (eventId)
            cache.invalidate(
              { __typename: 'Event', id: eventId },
              'eventCategoryMappingsByEventId',
            )
        },
        deleteEventFormatMappingByEventIdAndFormatId: (
          result,
          _args,
          cache,
          _info,
        ) => {
          const payload = result.deleteEventFormatMappingByEventIdAndFormatId
          if (!payload) return

          if (payload.deletedEventFormatMappingId)
            cache.invalidate({
              __typename: 'EventFormatMapping',
              id: payload.deletedEventFormatMappingId,
            })

          const eventId = payload.eventFormatMapping?.eventByEventId?.id
          if (eventId)
            cache.invalidate(
              { __typename: 'Event', id: eventId },
              'eventFormatMappingsByEventId',
            )
        },
        deletePreferenceEventCategoryByAccountIdAndCategoryId: (
          result,
          _args,
          cache,
          _info,
        ) =>
          cacheListRemove({
            cache,
            getItemDeletedId: (result) =>
              result.deletePreferenceEventCategoryByAccountIdAndCategoryId
                ?.deletedPreferenceEventCategoryId,
            getItemOfList: getPreferenceEventCategoryItem,
            listKey: 'allPreferenceEventCategories',
            query: allPreferenceEventCategoriesQuery,
            result,
          }),
        deletePreferenceEventFormatByAccountIdAndFormatId: (
          result,
          _args,
          cache,
          _info,
        ) =>
          cacheListRemove({
            cache,
            getItemDeletedId: (result) =>
              result.deletePreferenceEventFormatByAccountIdAndFormatId
                ?.deletedPreferenceEventFormatId,
            getItemOfList: getPreferenceEventFormatItem,
            listKey: 'allPreferenceEventFormats',
            query: allPreferenceEventFormatsQuery,
            result,
          }),
        deletePreferenceEventLocationByRowId: (result, _args, cache, _info) =>
          cacheListRemove({
            cache,
            getItemDeletedId: (result) =>
              result.deletePreferenceEventLocationByRowId
                ?.deletedPreferenceEventLocationId,
            getItemOfList: getPreferenceEventLocationItem,
            listKey: 'allPreferenceEventLocations',
            query: allPreferenceEventLocationsQuery,
            result,
          }),
        deleteProfilePictureByRowId: (_result, args, cache, _info) =>
          invalidateCache(cache, 'ProfilePicture', args),
      },
    },
  }

  const cacheStorage = import.meta.client ? makeDefaultStorage() : undefined

  // `getOfflineExchange` binds a fresh in-memory store to whichever `storage`
  // it's given at call time, so this has to be re-invoked (not just reused)
  // whenever the client is rebuilt - otherwise `urqlReset` recreates the
  // `Client`s but keeps serving normalized entities from the old store.
  const buildClientOptions = () => {
    const cacheExchange =
      import.meta.client && cacheStorage
        ? getOfflineExchange({
            ...graphCacheConfig,
            schema,
            storage: cacheStorage,
          })
        : undefined

    const clientOpts: ClientOptions = {
      ...clientOptions,
      exchanges: [
        ...(runtimeConfig.public.vio.isInProduction ? [] : [devtoolsExchange]),
        requestPolicyExchange({}),
        ...(cacheExchange ? [cacheExchange] : []),
        ssrExchange, // `ssrExchange` must be before `fetchExchange`
        fetchExchange,
      ],
      url: `${baseUrl}/api/service/postgraphile/graphql`,
    }

    return {
      clientOptions: clientOpts,
      clientOptionsTesting: {
        ...clientOpts,
        url: `${baseUrl}/api/test/service/postgraphile/graphql`,
      },
    }
  }

  const initial = buildClientOptions()
  const client = ref(createClient(initial.clientOptions))
  const clientTesting = ref(createClient(initial.clientOptionsTesting))

  const urqlResetInThisTab = async () => {
    // Drop persisted entities first so the rebuilt exchange below starts from an empty store instead of rehydrating the previous session's data.
    await cacheStorage?.clear()

    const next = buildClientOptions()
    client.value = createClient(next.clientOptions)
    clientTesting.value = createClient(next.clientOptionsTesting)
  }

  // Every tab holds its own in-memory store and writes it back to the shared storage on its next persist, so a reset that stops at the tab it was triggered in gets undone by the others.
  const resetChannel = import.meta.client
    ? new BroadcastChannel('urql-reset')
    : undefined

  resetChannel?.addEventListener('message', () => {
    void urqlResetInThisTab()
  })

  const urqlReset = async () => {
    // `postMessage` reaches the other tabs only, never the sender, so this tab still has to reset itself below.
    resetChannel?.postMessage(undefined)
    await urqlResetInThisTab()
  }

  return {
    ssrExchange,
    urql: client,
    urqlTesting: clientTesting,
    urqlCache: cacheStorage,
    urqlReset,
  }
}
